import { Injectable, OnDestroy } from '@angular/core';
import { IndexedDBManager } from '@examdojo/browser/indexed-db';
import { QuestionEditorState } from '@examdojo/models/question-editor';
import { QuestionType } from '@examdojo/review';
import { IDBPDatabase, IDBPTransaction } from 'idb';

const DB_NAME = 'examdojo-storage';
const CURRENT_VERSION = 2;

const QUESTION_ITEMS_STORE_NAME = 'question-items';

@Injectable()
export class QuestionItemsStorageService implements OnDestroy {
  constructor() {
    this.cleanupOldItems();
  }

  private saveDisabledLocks: string[] = [];

  private readonly storage = new IndexedDBManager<{
    [QUESTION_ITEMS_STORE_NAME]: QuestionEditorState;
  }>({
    dbName: DB_NAME,
    stores: {
      [QUESTION_ITEMS_STORE_NAME]: {},
    },
    version: CURRENT_VERSION,
    onUpgrade: (db, oldVersion, newVersion, transaction) => {
      this.runMigrations(db, oldVersion, newVersion, transaction);
    },
  });

  async ngOnDestroy() {
    await this.storage.close();
  }

  disableSaving(lockName: string): void {
    if (!this.saveDisabledLocks.includes(lockName)) {
      this.saveDisabledLocks.push(lockName);
    }
  }

  enableSaving(lockName: string): void {
    this.saveDisabledLocks = this.saveDisabledLocks.filter((lock) => lock !== lockName);
  }

  isSavingEnabled(): boolean {
    return this.saveDisabledLocks.length === 0;
  }

  getSaveDisabledLocks(): readonly string[] {
    return [...this.saveDisabledLocks];
  }

  async getQuestionState(keyParams: QuestionStateKeyParams): Promise<QuestionEditorState | null> {
    const key = this.createStorageKey(keyParams);
    return this.storage.getValue(QUESTION_ITEMS_STORE_NAME, key);
  }

  async saveQuestionState(state: QuestionEditorState, keyParams: QuestionStateKeyParams): Promise<void> {
    if (this.saveDisabledLocks.length > 0) {
      return;
    }

    const key = this.createStorageKey(keyParams);
    await this.storage.setValue(QUESTION_ITEMS_STORE_NAME, key, state);
  }

  async deleteQuestionState(keyParams: QuestionStateKeyParams): Promise<void> {
    const key = this.createStorageKey(keyParams);
    await this.storage.deleteValue(QUESTION_ITEMS_STORE_NAME, key);
  }

  async deleteAllQuestionStates(): Promise<void> {
    return this.storage.deleteAll(QUESTION_ITEMS_STORE_NAME);
  }

  private createStorageKey(key: QuestionStateKeyParams): string {
    return `${key.questionType}_${key.activeMode}_${key.activeQuestionId}`;
  }

  private async cleanupOldItems(): Promise<void> {
    try {
      const oneMonthAgo = Date.now() - 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
      const allKeys = await this.storage.getAllKeys(QUESTION_ITEMS_STORE_NAME);

      for (const key of allKeys) {
        const stored = await this.storage.getValue(QUESTION_ITEMS_STORE_NAME, key);
        // Remove items without timestamp (old format) or older than one month
        if (!stored || !stored.timestamp || stored.timestamp < oneMonthAgo) {
          await this.storage.deleteValue(QUESTION_ITEMS_STORE_NAME, key);
        }
      }
    } catch (error) {
      console.warn('Failed to cleanup old cached items:', error);
    }
  }

  private runMigrations(
    db: IDBPDatabase,
    oldVersion: number,
    newVersion: number,
    transaction: IDBPTransaction<unknown, string[], 'versionchange'>,
  ): void {
    console.debug(`Running migrations from version ${oldVersion} to ${newVersion}`);

    if (oldVersion < 2) {
      this.migrateToV2(db, transaction);
    }
  }

  private migrateToV2(db: IDBPDatabase, _transaction: IDBPTransaction<unknown, string[], 'versionchange'>): void {
    if (db.objectStoreNames.contains(QUESTION_ITEMS_STORE_NAME)) {
      db.deleteObjectStore(QUESTION_ITEMS_STORE_NAME);
      db.createObjectStore(QUESTION_ITEMS_STORE_NAME);
    }
  }
}

export interface QuestionStateKeyParams {
  questionType: QuestionType;
  activeMode: string;
  activeQuestionId: string | number;
}
